/**
 * «منشی هوشمند» — what came back, as notifications (PM-0080).
 *
 * A notification says what was understood and waits until it is opened or
 * dismissed: the command has already cost پارسه, so — unlike the kit's toasts,
 * one sentence that leaves after four seconds — it never leaves on a timer.
 * At most three show on a desk and two on a phone; the rest wait behind «+N».
 *
 * Desk: stacked beside the record button at the foot of the sidebar, newest
 * nearest the button — clear of the toasts, which own the opposite corner.
 * Phone: dropping from the top, newest first — the bottom already holds the bar,
 * the round button and the toasts.
 */
import React from 'react';
import {
  CalendarCheck, CalendarClock, Check, CircleHelp, Coins, FolderOpen, ListChecks, Lock, RotateCcw, TriangleAlert,
  UserCheck, Users, Wallet, X,
} from 'lucide-react';
import { soft } from '@parsina/framework/tokens';

import { C } from '../constants/theme';
import { useTranslation } from '../i18n';
import { Kbd } from '../lib/shortcuts.jsx';
import useResponsive from '../hooks/useResponsive.js';
import { useCreditPurchase } from '../hooks/useCreditPurchase.js';
import { FOLLOW_UP_KINDS, QUESTION_KINDS, resultPatient } from './model';

const TONES = {
  ok: () => C.teal,
  ask: () => C.amber,
  pick: () => C.navy,
  info: () => C.navy,
  warn: () => C.amber,
};

/**
 * What one result says in its notification. `canTopUp` false (credit is not sold
 * here, hooks/useCreditPurchase.js): an out-of-credit notice invites the reader
 * to open it, not to top up.
 */
export function describeNotice(item, tr, { canTopUp = true } = {}) {
  const { t, formatDate, formatTime, formatDigits, formatNumber, formatMoney } = tr;
  const r = item.result;
  if (item.error === 'credit') {
    return {
      tone: 'warn', Icon: Wallet, title: t('secretary.noCredit.title'),
      line: item.text ? t('secretary.noCredit.afterRun') : t('secretary.noCredit.notStarted'),
      action: canTopUp ? t('secretary.notice.action.topUp') : t('secretary.notice.action.details'),
    };
  }
  if (item.error === 'failed' || !r) {
    return { tone: 'warn', Icon: RotateCcw, title: t('secretary.notice.failed'), line: t('secretary.notice.failedLine'), action: t('secretary.notice.action.retry') };
  }
  const patient = resultPatient(r);
  const name = patient?.name || r.heardName || r.detail || '';
  switch (r.outcome) {
    case 'PROPOSAL':
      if (r.kind === 'book_appointment' && r.booking) {
        const b = r.booking;
        const first = b.slots?.[0];
        const line = first
          ? t('secretary.notice.bookLine', { day: formatDate(first.dateTime, 'weekdayDayMonth'), time: formatTime(first.dateTime), doctor: b.doctor.name })
          : b.doctor.name;
        const asking = (r.questions || []).filter((q) => q?.blocking && QUESTION_KINDS.includes(q.kind));
        if (asking.length) {
          return {
            tone: 'ask', Icon: CalendarCheck,
            title: b.patient.isNew ? t('secretary.notice.bookNew', { name: b.patient.name }) : t('secretary.notice.bookFor', { name: b.patient.name }),
            line,
            badge: t('secretary.notice.questions', {
              count: formatNumber(asking.length),
              list: asking.map((q) => t(`secretary.notice.ask.${q.kind}`)).join(t('secretary.newPatient.and')),
            }),
            action: t('secretary.notice.action.answer'),
          };
        }
        return { tone: 'ok', Icon: CalendarCheck, title: t('secretary.notice.bookReady', { name: b.patient.name }), line, action: t('secretary.notice.action.form') };
      }
      if (r.kind === 'mark_arrived' && r.arrival) {
        const a = r.arrival;
        const line = !a.dateTime ? ''
          : a.doctorName ? t('secretary.arrival.today', { time: formatTime(a.dateTime), doctor: a.doctorName })
            : t('secretary.arrival.todayNoDoctor', { time: formatTime(a.dateTime) });
        return { tone: 'ok', Icon: UserCheck, title: t('secretary.notice.arrive', { name: a.patient.name }), line, action: t('secretary.notice.action.arrive') };
      }
      if (r.kind === 'follow_up' && r.followUp) {
        const f = r.followUp;
        const kind = t(`patients.followUp.kind.${FOLLOW_UP_KINDS.includes(f.kind) ? f.kind : 'other'}`);
        return {
          tone: f.dueAt ? 'ok' : 'ask', Icon: CalendarClock, title: t('secretary.notice.followUp', { name: f.patient.name }),
          line: f.dueAt ? t('secretary.notice.followUpLine', { kind, day: formatDate(f.dueAt, 'weekdayDayMonth') }) : t('secretary.notice.followUpNoDay', { kind }),
          badge: f.dueAt ? null : t('secretary.notice.questions', { count: formatNumber(1), list: t('secretary.notice.ask.followUpDate') }),
          action: t('secretary.notice.action.followUp'),
        };
      }
      break;
    case 'NO_SLOT':
      return {
        tone: 'warn', Icon: CalendarClock, title: t('secretary.notice.noSlot'),
        line: [r.booking?.patient?.name, r.booking?.doctor?.name].filter(Boolean).join(' · '),
        action: t('secretary.notice.action.form'),
      };
    case 'CHOOSE_PATIENT': {
      const count = formatNumber(r.patients?.length || 0);
      const offersNew = r.kind === 'book_appointment' && Boolean(r.heardName);
      return {
        tone: 'pick', Icon: Users,
        title: r.heardName ? t('secretary.notice.choosePatient', { name: r.heardName }) : t('secretary.choosePatient.title'),
        line: offersNew ? t('secretary.notice.candidatesOrNew', { count }) : t('secretary.notice.candidates', { count }),
        badge: t('secretary.notice.pick'), action: t('secretary.notice.action.choose'),
      };
    }
    case 'CHOOSE_DOCTOR':
      return {
        tone: 'pick', Icon: Users, title: t('secretary.question.title.doctor'),
        line: name ? t('secretary.notice.bookFor', { name }) : '', badge: t('secretary.notice.pick'), action: t('secretary.notice.action.choose'),
      };
    case 'NOT_FOUND': {
      const said = r.detail;
      const title = !said ? t('secretary.notFound.titleGeneric')
        : r.reason === 'not_booked_today' ? t('secretary.notFound.notBookedToday', { name: said })
          : r.reason === 'no_such_patient' ? t('secretary.notFound.noSuchPatient', { name: said })
            : t('secretary.notFound.title', { name: said });
      return { tone: 'warn', Icon: CircleHelp, title, line: r.patients?.length ? t('secretary.notice.closest') : '', action: t('secretary.notice.action.details') };
    }
    case 'NOTHING_TO_DO':
      return { tone: 'info', Icon: Check, title: t('secretary.arrival.nothingTitle'), line: r.arrival?.patient?.name || '', action: t('secretary.notice.action.details') };
    case 'NAVIGATE':
      return {
        tone: 'ok', Icon: FolderOpen, title: t('secretary.notice.openRecord', { name }),
        line: patient?.fileNumber ? t('secretary.choosePatient.file', { file: formatDigits(patient.fileNumber) }) : '',
        action: t('secretary.notice.action.record'),
      };
    case 'ANSWER':
      if (r.kind === 'patient_balance' && r.balance) {
        const b = r.balance;
        return {
          tone: 'info', Icon: Coins,
          title: b.outstanding > 0
            ? t('secretary.notice.balance', { name: b.patient.name, amount: formatMoney(b.outstanding, { currency: b.currency, withUnit: true }) })
            : t('secretary.notice.balanceNone', { name: b.patient.name }),
          line: b.outstanding > 0 ? t('secretary.balance.openInvoices', { count: formatNumber(b.openInvoices) }) : '',
          action: t('secretary.notice.action.details'),
        };
      }
      if (r.kind === 'day_summary' && r.daySummary) {
        const d = r.daySummary;
        return {
          tone: 'info', Icon: ListChecks, title: t('secretary.notice.daySummary', { total: formatNumber(d.total) }),
          line: t('secretary.notice.daySummaryLine', { arrived: formatNumber(d.arrived), completed: formatNumber(d.completed), scheduled: formatNumber(d.scheduled) }),
          action: t('secretary.notice.action.details'),
        };
      }
      break;
    case 'FORBIDDEN':
      return { tone: 'warn', Icon: Lock, title: t('secretary.forbidden.title'), line: t('secretary.forbidden.hint'), action: t('secretary.notice.action.details') };
    case 'OUT_OF_SCOPE':
      return { tone: 'warn', Icon: TriangleAlert, title: t('secretary.outOfScope.title'), line: '', action: t('secretary.notice.action.details') };
    case 'UNAVAILABLE':
      return { tone: 'warn', Icon: CircleHelp, title: t('secretary.unavailable.title'), line: '', action: t('secretary.notice.action.details') };
    default:
      break;
  }
  return { tone: 'warn', Icon: CircleHelp, title: t('secretary.unknown.title'), line: t('secretary.notice.unknownLine'), action: t('secretary.notice.action.fix') };
}

function Notice({ item, newest, onOpen, onDismiss, tr, isMobile }) {
  const { t, formatTime } = tr;
  const canTopUp = useCreditPurchase(item.error === 'credit');
  const d = describeNotice(item, tr, { canTopUp });
  const tone = TONES[d.tone]?.() || C.navy;
  const { Icon } = d;
  return (
    <div
      role="group"
      aria-label={d.title}
      data-testid={`secretary-notice-${item.id}`}
      data-tone={d.tone}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 4, padding: 4, borderRadius: 14,
        background: C.card, border: `1px solid ${C.border}`,
        boxShadow: '0 10px 28px rgba(13, 27, 38, 0.16), 0 1px 3px rgba(13, 27, 38, 0.10)',
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-keyshortcuts={newest && !isMobile ? 'Shift+F1' : undefined}
        data-testid="secretary-notice-open"
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 8px 8px 4px',
          border: 0, borderRadius: 11, background: 'transparent', color: C.textPrimary, textAlign: 'start',
          fontFamily: 'inherit', cursor: 'pointer',
        }}
      >
        <span aria-hidden="true" style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 11, display: 'grid', placeItems: 'center', background: soft(tone, 0.14), color: tone }}>
          <Icon size={18} />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 1 }}>
          <span dir="auto" style={{ fontSize: '0.875rem', fontWeight: 800, lineHeight: 1.45 }} data-testid="secretary-notice-title">{d.title}</span>
          {d.line ? <span dir="auto" style={{ fontSize: '0.78125rem', color: C.textSecondary }}>{d.line}</span> : null}
          {item.text ? (
            <span dir="auto" style={{ fontSize: '0.75rem', color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('secretary.notice.heard', { text: item.text })}
            </span>
          ) : null}
          <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', marginTop: 5 }}>
            {d.badge ? (
              <span style={{ fontSize: '0.71875rem', fontWeight: 700, padding: '0 9px', borderRadius: 999, background: soft(tone, 0.14), color: tone, whiteSpace: 'nowrap' }} data-testid="secretary-notice-badge">
                {d.badge}
              </span>
            ) : null}
            <span style={{ fontSize: '0.78125rem', fontWeight: 800, color: C.navy, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {d.action}
              {newest && !isMobile ? <Kbd combo="shift+f1" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.textSecondary }} /> : null}
            </span>
            <span style={{ marginInlineStart: 'auto', fontSize: '0.71875rem', color: C.textMuted }}>{formatTime(item.createdAt)}</span>
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('secretary.notice.dismiss')}
        title={t('secretary.notice.dismiss')}
        data-testid="secretary-notice-dismiss"
        style={{
          width: 28, height: 28, flexShrink: 0, marginTop: 4, border: 0, borderRadius: 8, background: 'transparent',
          color: C.textMuted, display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}
      >
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  );
}

export default function SecretaryNotices({ items, onOpen, onDismiss, sidebarWidth = 0 }) {
  const tr = useTranslation();
  const { isMobile } = useResponsive();
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => { if (items.length <= (isMobile ? 2 : 3)) setExpanded(false); }, [items.length, isMobile]);
  if (!items.length) return null;
  const max = isMobile ? 2 : 3;
  const newestFirst = [...items].reverse();
  const shown = expanded ? newestFirst : newestFirst.slice(0, max);
  const rest = newestFirst.length - shown.length;
  // Newest nearest the button: last in the column on a desk, first on a phone.
  const ordered = isMobile ? shown : [...shown].reverse();
  const more = rest > 0 ? (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      data-testid="secretary-notices-more"
      style={{
        alignSelf: 'center', border: `1px solid ${C.border}`, borderRadius: 999, background: C.card, color: C.textSecondary,
        fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, padding: '2px 12px', cursor: 'pointer',
      }}
    >
      {tr.t('secretary.notice.more', { count: tr.formatNumber(rest) })}
    </button>
  ) : null;
  const place = isMobile
    ? { position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 10px)', insetInline: 10, zIndex: 85 }
    : { position: 'fixed', bottom: 16, insetInlineStart: sidebarWidth + 16, width: 360, zIndex: 60 };
  return (
    <section
      aria-label={tr.t('secretary.notice.listLabel')}
      data-testid="secretary-notices"
      style={{
        ...place, display: 'flex', flexDirection: 'column', gap: 10,
        maxHeight: 'calc(100dvh - 32px)', overflowY: expanded ? 'auto' : 'visible',
      }}
    >
      {isMobile ? null : more}
      {ordered.map((item) => (
        <Notice
          key={item.id}
          item={item}
          newest={item === newestFirst[0]}
          onOpen={() => onOpen(item)}
          onDismiss={() => onDismiss(item.id)}
          tr={tr}
          isMobile={isMobile}
        />
      ))}
      {isMobile ? more : null}
    </section>
  );
}
